import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as MfaActions from './mfa.actions';
import { MfaService } from '../../http/mfa.service';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, from, of } from 'rxjs';
import { Router } from '@angular/router';
import * as UserSessionActions from '../user-session/user-session.actions';
import {
  getSession,
  switchToProduct,
} from '../user-session/user-session.actions';
import { RootState } from '../shared/root-state.interface';
import { select, Store } from '@ngrx/store';
import {
  selectMfaOriginUrl,
  selectMfaOriginUserProfileData,
} from './mfa.selectors';
import {
  MfaOriginProductModel,
  ProductModel,
  Products,
  TwoFactorTypes,
} from '../../models/user-profile.model';
import { MfaErrors } from './mfa.state';

export const getIsMfaEnabledEffect = createEffect(
  (
    actions$ = inject(Actions),
    mfaService = inject(MfaService),
    router = inject(Router),
  ) => {
    return actions$.pipe(
      ofType(MfaActions.getIsMfaEnabled),
      switchMap((action) =>
        mfaService.getIsMfaEnabled(action.mfaToken).pipe(
          switchMap((userData) => {
            if (userData.twoFactorDelay) {
              return of(
                switchToProduct({
                  product: getSwitchParams(userData.originProduct),
                  isRedirect: true,
                }),
              );
            }
            return from(
              router.navigate([
                userData.twoFactorEnabled
                  ? '/two-factor'
                  : '/set-up-two-factor',
              ]),
            ).pipe(
              map(() =>
                MfaActions.promptMfa({
                  originProduct: userData.originProduct,
                  originUrl: action.originUrl,
                  twoFactorType: userData.twoFactorType as TwoFactorTypes,
                }),
              ),
            );
          }),
          catchError((error) =>
            of(
              MfaActions.mfaError({
                errorType:
                  error.status === 417
                    ? MfaErrors.IS_EMAIL_MISSING
                    : MfaErrors.DEFAULT,
                originUrl: action.originUrl,
              }),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const authenticateMfaEffect = createEffect(
  (actions$ = inject(Actions), mfaService = inject(MfaService)) =>
    actions$.pipe(
      ofType(MfaActions.authenticateMfaCode),
      switchMap((action) =>
        mfaService.authenticateMfaCode(action.code, action.rememberUser).pipe(
          map((userData) =>
            MfaActions.navigateToOrigin({
              originProduct: userData.originProduct,
            }),
          ),
          catchError(() => of(MfaActions.mfaError({}))),
        ),
      ),
    ),
  { functional: true },
);

export const navigateToOriginEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<RootState>),
    router = inject(Router),
  ) =>
    actions$.pipe(
      ofType(MfaActions.navigateToOrigin),
      withLatestFrom(store.pipe(select(selectMfaOriginUserProfileData))),
      switchMap(([action, originProductData]) => {
        const originProduct: MfaOriginProductModel =
          action.originProduct || originProductData;
        if (!originProduct.code || originProduct.code === 'UP') {
          router.navigate(['/home']);
          return of(getSession());
        } else {
          const switchParams = getSwitchParams(originProduct);
          return of(
            switchToProduct({
              product: switchParams,
              isRedirect: true,
            }),
          );
        }
      }),
    ),
  { functional: true },
);

export const navigateToOriginLoginEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store<RootState>),
    router = inject(Router),
  ) =>
    actions$.pipe(
      ofType(MfaActions.navigateToOriginLogin),
      withLatestFrom(store.pipe(select(selectMfaOriginUrl))),
      switchMap(([action, originUrl]) => {
        if (originUrl) {
          window.open(originUrl, '_self');
        } else {
          router.navigate(['/login']);
          if (action.isTimedOut) {
            return of(
              UserSessionActions.logoutSuccess({
                isTimedOut: true,
              }),
            );
          }
        }
        return EMPTY;
      }),
    ),
  { functional: true },
);

function getSwitchParams({
  code,
  userid,
  loginUrl,
}: MfaOriginProductModel): ProductModel {
  const baseProduct = Products[code];
  return {
    ...baseProduct,
    userid,
    loginUrl,
  };
}
